import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, updateDoc, increment, onSnapshot, arrayUnion, getDoc, runTransaction } from 'firebase/firestore';
import { collection } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';

interface CollectiveInvestmentContextType {
  totalInvestment: number;
  participants: number;
  drawCount: number;
  hasParticipated: boolean;
  updateInvestment: (amount: number, userId: string) => Promise<boolean>;
  incrementDrawCount: () => Promise<void>;
  resetInvestments: () => Promise<void>;
}

const CollectiveInvestmentContext = createContext<CollectiveInvestmentContextType | undefined>(undefined);

export function CollectiveInvestmentProvider({ children }: { children: React.ReactNode }) {
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [participants, setParticipants] = useState(0);
  const [drawCount, setDrawCount] = useState(0);
  const [participantsList, setParticipantsList] = useState<string[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'collectiveInvestment', 'current'), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setTotalInvestment(data.totalAmount || 0);
        setParticipants(data.participantsCount || 0);
        setParticipantsList(data.participants || []);
        setDrawCount(data.drawCount || 0);
      } else {
        setTotalInvestment(0);
        setParticipants(0);
        setParticipantsList([]);
        setDrawCount(0);
      }
    }, (error) => {
      console.error("Erreur du listener:", error);
    });

    return () => unsubscribe();
  }, []);

  const updateInvestment = async (amount: number, userId: string) => {
    try {
      const participationDoc = await getDoc(doc(db, 'drawSettings', 'participation'));
      if (participationDoc.exists() && !participationDoc.data().enabled) {
        throw new Error('Les participations sont actuellement désactivées');
      }

      await runTransaction(db, async (transaction) => {
        const investmentRef = doc(db, 'collectiveInvestment', 'current');
        const walletRef = doc(db, 'wallets', userId);
        
        const investmentDoc = await transaction.get(investmentRef);
        const walletDoc = await transaction.get(walletRef);

        if (!investmentDoc.exists() || !walletDoc.exists()) {
          throw new Error('Documents requis non trouvés');
        }

        const currentData = investmentDoc.data();
        const currentParticipants = currentData.participants || [];
        const isNewParticipant = !currentParticipants.includes(userId);

        const walletBalance = walletDoc.data().balance || 0;
        if (walletBalance < amount) {
          throw new Error('Solde insuffisant');
        }

        transaction.update(investmentRef, {
          totalAmount: increment(amount),
          participantsCount: isNewParticipant ? increment(1) : increment(0),
          participants: isNewParticipant ? arrayUnion(userId) : arrayUnion(),
          lastUpdated: new Date()
        });

        transaction.update(walletRef, {
          balance: increment(-amount)
        });

        const transactionRef = doc(collection(db, 'users', userId, 'transactions'));
        transaction.set(transactionRef, {
          type: 'investment',
          amount: -amount,
          date: new Date(),
          title: 'Participation aux donations collectives',
          status: 'completed'
        });
      });

      return true;
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'investissement:", error);
      throw error;
    }
  };

  const incrementDrawCount = async () => {
    const investmentRef = doc(db, 'collectiveInvestment', 'current');
    await updateDoc(investmentRef, {
      drawCount: increment(1)
    });
  };

  const resetInvestments = async () => {
    try {
      const investmentRef = doc(db, 'collectiveInvestment', 'current');
      await updateDoc(investmentRef, {
        totalAmount: 0,
        participantsCount: 0,
        participants: [],
        lastUpdated: new Date()
      });
      console.log('Réinitialisation effectuée avec succès');
    } catch (error) {
      console.error("Erreur lors de la réinitialisation des investissements:", error);
      throw error;
    }
  };

  return (
    <CollectiveInvestmentContext.Provider value={{ 
      totalInvestment, 
      participants, 
      drawCount,
      hasParticipated: participantsList.includes(user?.id || ''),
      updateInvestment, 
      incrementDrawCount,
      resetInvestments
    }}>
      {children}
    </CollectiveInvestmentContext.Provider>
  );
}

export function useCollectiveInvestment() {
  const context = useContext(CollectiveInvestmentContext);
  if (context === undefined) {
    throw new Error('useCollectiveInvestment must be used within a CollectiveInvestmentProvider');
  }
  return context;
}